/**
 * The timeline: how a view's history is written and shown.
 *
 * Every view has a timeline, its history, along ticks: numbers from 0 that only say what comes
 * first, with any fraction between two, and no length of their own. Every `at`, `from` and `until`
 * written on it is a tick. It only grows from where it ends: an extension continues it to a later
 * end, and a clarification writes where it ends without moving it. Nothing written is taken back.
 *
 * Dates are labels on ticks. Players read the label at a tick, and a tick without one has no date.
 * A scene shows any stretch written so far as a short film with seconds of its own. An input asks a
 * player something, and since the answer may change what happens, nothing more is written on their
 * timeline until they answer.
 */
import { z } from "zod";
import { FilterSchema } from "./filters";
import { IdSchema, listOf, NameSchema } from "./primitives";
import { animated, CameraSchema, type Change, PictureSchema, ThingSchema } from "./things";

/** A place on the timeline. */
export const TickSchema = z.number().nonnegative();

/** A date as players read it, shown as written, in whatever terms the world keeps time. */
export const GameDateSchema = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .meta({ description: "A date as players read it, shown as written." });

/**
 * The date players read at one tick. Every tick a round begins at has one, and any other may.
 * Nothing is worked out from labels: a tick without one has no date, whatever the ticks around it
 * say. A label at a tick that has one replaces it.
 */
export const LabelSchema = z.strictObject({ tick: TickSchema, date: GameDateSchema });
export type Label = z.infer<typeof LabelSchema>;

/** What a write puts on the timeline, every tick of it in the stretch the write covers. */
const written = {
  labels: listOf(LabelSchema).optional(),
  /** What it places. A thing without a `from` appears where the write starts. */
  things: z.array(ThingSchema).optional(),
  /**
   * Changes to things by id, on the map or placed here. A change says what some of a thing's
   * properties are from its `at`, or from where the write starts, and before then the thing is as
   * it was. Each property given replaces the whole of what it was, and keys given run on from what
   * it is there. A thing may change at several ticks, once at each, and the keys of each run only to
   * its next. Nothing a thing changes into enters or exits: it goes on as the thing it was.
   */
  changes: z.array(z.looseObject({ id: IdSchema, at: TickSchema.optional() })).optional(),
};

/**
 * The timeline continued from where it ends to `end`, which is later. What is written where it
 * ends without moving it is a clarification.
 */
export const TimelineExtensionSchema = z.strictObject({ end: TickSchema, ...written });
export type TimelineExtension = Omit<z.infer<typeof TimelineExtensionSchema>, "changes"> & {
  changes?: Change[];
};

/**
 * What a conversation settles, written where the timeline ends without moving it. The Timeline
 * shows it as a step of its own.
 */
export const ClarificationSchema = z
  .strictObject({
    /** What it settles, as the Timeline names it, such as the treaty it records. */
    name: NameSchema.optional(),
    ...written,
  })
  .refine(
    clarification =>
      (clarification.labels?.length ?? 0) > 0 ||
      (clarification.things?.length ?? 0) > 0 ||
      (clarification.changes?.length ?? 0) > 0,
    "A clarification labels, places, or changes something.",
  );
export type Clarification = Omit<z.infer<typeof ClarificationSchema>, "changes"> & {
  changes?: Change[];
};

/**
 * Things of the timeline beneath a scene turned down for a while: those its `filter` holds for, or
 * all of them without one, shown at `opacity` times however strongly they show already, so what the
 * timeline does with them goes on beneath. It comes in over half a second up to `from`, or as much of
 * one as the scene has before it, and goes over half a second after `until`. Dims that overlap
 * multiply, and a scene's own things are never dimmed. A scene shows a thing of the timeline
 * differently by dimming it to 0 and drawing its own version among its things.
 * `{ filter: ["all", ["==", ["get", "side"], "axis"], ["!", ["in", ["id"], ["literal", ["u:de-vii",
 * "u:de-ix"]]]]], opacity: 0.25, from: 0, until: 8 }` dims every Axis unit but two corps for the
 * scene's first eight seconds.
 */
export const DimSchema = z
  .strictObject({
    filter: FilterSchema.optional(),
    /** How strongly they show, 0 to 1, times how strongly they show already: 0 hides them. */
    opacity: z.number().min(0).max(1),
    /** Seconds of the scene. */
    from: z.number().nonnegative(),
    until: z.number().nonnegative(),
  })
  .refine(dim => dim.from < dim.until, "A dim ends after it begins.");
export type Dim = z.infer<typeof DimSchema>;

/**
 * Everything beneath a scene darkened, its basemap too, but for soft circles `km` round the points
 * it names, wherever they stand: for drawing the eye. It comes in over half a second up to `from`,
 * or as much of one as the scene has before it, and goes over half a second after `until`.
 */
export const SpotlightSchema = z
  .strictObject({
    points: listOf(IdSchema).min(1),
    km: z.number().positive(),
    from: z.number().nonnegative(),
    until: z.number().nonnegative(),
  })
  .refine(spotlight => spotlight.from < spotlight.until, "A spotlight ends after it begins.");
export type Spotlight = z.infer<typeof SpotlightSchema>;

/** Something that came of an event: a few words of Markdown, and a picture where it has one. */
export const OutcomeSchema = z.strictObject({
  /** A few words of Markdown. */
  text: z.string().trim().min(1).max(120),
  /** A Game Icons icon or an asset, as a board entry's picture names one. */
  picture: PictureSchema.optional(),
});
export type Outcome = z.infer<typeof OutcomeSchema>;

/**
 * One step of a scene, played on its own while the player reads its `story`: from where the event
 * before it ends, or from the scene's start, to its `until`. Once it has played, the map holds its
 * last moment until the player presses Next event.
 */
export const SceneEventSchema = z.strictObject({
  /**
   * New to every view that sees it. Threads and messages, advice, and changes to perspectives and
   * aliases sent with it name it by `eventId`, and arrive as it plays.
   */
  id: IdSchema,
  /** The second of the scene it ends at. */
  until: z.number().positive(),
  /**
   * When it happens, as its header says it: "12 August, dawn", "That night", or "Spring 1915".
   * It is shown as written, and has nothing to do with ticks or labels.
   */
  when: z.string().trim().min(1).max(200),
  /** What the player reads while it plays, in Markdown. */
  story: z.string().max(100_000),
  /**
   * What leads into it from the event before, in your own words, such as "Two days earlier",
   * "Meanwhile" or "In Berlin". Leave it out where nothing needs saying.
   */
  transition: NameSchema.optional(),
  /**
   * What came of it, shown as written under its story. They point at nothing on the map or a
   * board: what changes there is written on the timeline.
   */
  outcomes: z.array(OutcomeSchema).max(8).optional(),
});
export type SceneEvent = z.infer<typeof SceneEventSchema>;

/**
 * One story at one focus, told on the map as a short film with seconds of its own: its events play
 * back to back from second 0, and it lasts until its last event ends. Its tick, camera, things,
 * dims and spotlights are keyed in those seconds. It decides nothing: it draws over the timeline and
 * turns it down, and leaves it as it was. The Timeline shows a scene whole, under its title.
 */
export const SceneSchema = z.strictObject({
  /** New to every view that sees it. */
  id: IdSchema,
  title: NameSchema,
  /** Its events in the order they play, each ending later in the scene than the one before. */
  events: listOf(SceneEventSchema)
    .min(1)
    .refine(
      events =>
        events.every(
          (event, index) => index === 0 || event.until > (events[index - 1]?.until ?? 0),
        ),
      "Each event ends later in the scene than the one before.",
    ),
  /**
   * The tick on screen at each of its seconds: one tick, or keys that hold (`step`) or move
   * (`linear`, `smooth`) between ticks. It may be any tick written so far: a tick earlier than the
   * one before goes back, cut to as soon as the key before it is passed, and the whole map beneath
   * plays as it was then, so two scenes may show the same stretch, such as the Eastern Front after
   * the Western.
   */
  tick: animated(TickSchema),
  camera: CameraSchema.optional(),
  /**
   * Things that show only while it plays, leaving as it ends whatever their `until`: texts and
   * cards that explain, arrows, markers, or a canvas of its own, such as a council chamber with its
   * people as points and their words as texts in the `speech` style on them, which the camera goes
   * to with a shot of it. They decide nothing; what lasts is written on the timeline.
   */
  things: z.array(ThingSchema).optional(),
  dims: z.array(DimSchema).optional(),
  spotlights: z.array(SpotlightSchema).optional(),
});
export type Scene = Omit<z.infer<typeof SceneSchema>, "things"> & {
  things?: z.infer<typeof ThingSchema>[];
};

/** How long a scene plays on screen, in seconds: until its last event ends. */
export function sceneSeconds(scene: Pick<Scene, "events">): number {
  return scene.events.at(-1)?.until ?? 0;
}

/** A scene's story whole, for reading rather than watching: each event's in turn, under its header. */
export function sceneStory(scene: Pick<Scene, "events">): string {
  return scene.events
    .map(event => {
      const header = event.transition ? `${event.transition}, ${event.when}` : event.when;
      return `${header}: ${event.story}`;
    })
    .join("\n\n");
}

/** Where each of a scene's events begins and ends, in the scene's seconds. */
export function eventSpans(
  scene: Pick<Scene, "events">,
): { event: SceneEvent; from: number; until: number }[] {
  return scene.events.map((event, index) => ({
    event,
    from: scene.events[index - 1]?.until ?? 0,
    until: event.until,
  }));
}

/**
 * What a player is asked: a choice to make, or a reply to write in one of its threads. With
 * `skippable`, the player may pass instead. Each perspective its delta reaches is asked, during a
 * jump, and until it answers or the input is withdrawn, nothing is written on its timeline: what it
 * says may change what happens. Scenes, messages and advice still reach it.
 */
export const InputSchema = z.discriminatedUnion("type", [
  z
    .strictObject({
      type: z.literal("choice"),
      prompt: z.string().trim().min(1).max(20_000),
      options: listOf(NameSchema).optional(),
      /** Whether the player may answer in their own words. */
      freeform: z.boolean().optional(),
      skippable: z.boolean().optional(),
    })
    .refine(choice => choice.freeform === true || (choice.options?.length ?? 0) > 0, {
      path: ["options"],
      message: "A choice that is not freeform needs at least one option.",
    }),
  z.strictObject({
    type: z.literal("reply"),
    threadId: IdSchema,
    skippable: z.boolean().optional(),
  }),
]);
export type Input = z.infer<typeof InputSchema>;

/** A player's answer to the input their perspective waits on. A reply is answered by writing in its thread. */
export const InputAnswerSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("skip") }),
  z
    .strictObject({
      type: z.literal("choice"),
      option: NameSchema.optional(),
      text: z.string().trim().min(1).max(20_000).optional(),
    })
    .refine(answer => answer.option !== undefined || answer.text !== undefined, {
      message: "A choice needs an option or text.",
    }),
]);
export type InputAnswer = z.infer<typeof InputAnswerSchema>;

export const TimelineOperationSchema = z.discriminatedUnion("type", [
  z.strictObject({ type: z.literal("timeline.extended"), extension: TimelineExtensionSchema }),
  z.strictObject({ type: z.literal("timeline.clarified"), clarification: ClarificationSchema }),
  z.strictObject({ type: z.literal("scene.shown"), scene: SceneSchema }),
  z.strictObject({ type: z.literal("input.asked"), input: InputSchema }),
  z.strictObject({ type: z.literal("input.answered"), answer: InputAnswerSchema }),
  z.strictObject({ type: z.literal("input.withdrawn") }),
]);
export type TimelineOperation =
  | { type: "timeline.extended"; extension: TimelineExtension }
  | { type: "timeline.clarified"; clarification: Clarification }
  | { type: "scene.shown"; scene: Scene }
  | { type: "input.asked"; input: Input }
  | { type: "input.answered"; answer: InputAnswer }
  | { type: "input.withdrawn" };
